import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { SourcingOpenings, SourcingService } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import TrashIcon from '@/assets/icons/trash.svg';
import { useToaster } from '@/components/base/toaster';
import { PlaneSendIcon } from '@/components/icons/plane-send';
import { ActionToast } from '@/components/ui/action-toast';
import { useAuthContext } from '@/contexts/auth-context';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import type { IneligibleOpening } from '@/components/pages/job-posting/select-channels-dialog/types';

interface RowsSelectProps {
  table: Table<SourcingOpenings>;
  refetchOpenings: () => Promise<void>;
}

export function RowsSelect({ table, refetchOpenings }: RowsSelectProps) {
  const queryClient = useQueryClient();
  const { rowSelection } = table.getState();
  const { notifySuccess, notifyError } = useToaster();
  const intl = useIntl();
  const { canPostToJobBoards } = useAuthContext();
  const {
    setOpenChannelsDialog,
    setSelectedOpeningIds,
    setIneligibleOpenings,
  } = useJobPostingContext();

  const getInactiveOpenings = useCallback(
    (rows: SourcingOpenings[]): IneligibleOpening[] =>
      rows
        .filter(row => !row.active || !row.is_sourcing_funnel)
        .map(row => ({
          externalId: row.external_id,
          title: row.title,
          reason: !row.active ? 'inactive' : 'sourcing_disabled',
          brandSlug: row.brand_slug ?? 'no_brand',
          locationId: row.location_id,
        })),
    [],
  );

  const selectedIds = useMemo(
    () => (_isEmpty(rowSelection) ? [] : Object.keys(rowSelection)),
    [rowSelection],
  );

  const inactiveOpenings = useMemo(() => {
    const selectedRows = table
      .getFilteredSelectedRowModel()
      .rows.map(r => r.original);
    return getInactiveOpenings(selectedRows);
  }, [getInactiveOpenings, table, rowSelection]);

  const { mutate: updateSelection } = useMutation({
    mutationFn: () =>
      SourcingService.postInternalApiSourcingOpeningsMarkForSourcing({
        unselected_opening_ids: selectedIds,
      }),
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Selected openings updated successfully!',
          description:
            'Success toast after bulk add/remove on sourcing dashboard',
        }),
      });
      table.resetRowSelection();
      await refetchOpenings();
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.dashboard.recommendations.sourcing],
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update the selected openings!',
          description: 'Error toast when bulk dashboard selection update fails',
        }),
      });
    },
  });

  const onSelectedOpeningsSubmit = useCallback(() => {
    updateSelection();
  }, [updateSelection]);

  const handlePostJobClick = useCallback(() => {
    const inactiveExternalIds = new Set(
      inactiveOpenings.map(opening => opening.externalId),
    );
    setIneligibleOpenings(inactiveOpenings);
    setSelectedOpeningIds(
      selectedIds.filter(id => !inactiveExternalIds.has(id)),
    );
    setOpenChannelsDialog(true);
  }, [
    selectedIds,
    setSelectedOpeningIds,
    setIneligibleOpenings,
    inactiveOpenings,
    setOpenChannelsDialog,
  ]);

  const actionButtons = useMemo(() => {
    const buttons = [];

    // Only show post job button if user has permission
    if (canPostToJobBoards) {
      buttons.push({
        label: intl.formatMessage({
          defaultMessage: 'Post job',
          description:
            'Bulk action: open post-to-channels for selected openings',
        }),
        onClick: handlePostJobClick,
        icon: <PlaneSendIcon className="text-fx-neutral-400" />,
      });
    }

    buttons.push({
      label: intl.formatMessage({
        defaultMessage: 'Remove selection from dashboard',
        description:
          'Bulk action: unpin selected roles from the sourcing dashboard',
      }),
      onClick: onSelectedOpeningsSubmit,
      icon: <img src={TrashIcon} alt="Chart bar" />,
    });

    return buttons;
  }, [canPostToJobBoards, handlePostJobClick, intl, onSelectedOpeningsSubmit]);

  if (_isEmpty(selectedIds)) return <></>;

  return <ActionToast actionButtons={actionButtons} />;
}
